import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from 'components-stateless/Spinner';
import { getUser } from 'auth';
import { allPermissions as userPermissions } from 'constants/userPermissions';
import { useStore } from 'hooks/useStore';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_TICKETING_MESSAGE_TIMELINE } from 'utils/flags/constants';
import { REQUESTER_REDIRECT_SOURCE_SEARCH_PARAM } from './constants';
import { useGetTicketingTicketMembershipsQuery } from '../_api/useGetTicketingTicketMembership';
import { buildTicketingRedirectUrl } from '../Boards/Tickets/Ticket/helpers/buildTicketingRedirectUrl';
import { hasPermission } from '../_api/useGetTicketingBoardMembership/types';
import { TicketingCollaboratorTypes } from '../Collaborators/types';

const hasAgentPermission = () => {
  const user = getUser();

  return Boolean(
    user &&
      user.permissions?.includes(userPermissions.matterAgentPermissions.code)
  );
};

export const TicketRedirectHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTimelineEnabled = useFlag(FEATURE_TICKETING_MESSAGE_TIMELINE);

  const ticketId = searchParams.get('ticketId') || '';
  const rawThreadId = searchParams.get('threadId');
  // When timeline is enabled, strip threadId — redirect to ticket-level timeline
  const threadId = isTimelineEnabled ? null : rawThreadId;
  const source = searchParams.get(REQUESTER_REDIRECT_SOURCE_SEARCH_PARAM);
  const tab = searchParams.get('tab');
  const type = searchParams.get('type');

  const isAgent = hasAgentPermission();

  const addNotificationError = useStore(
    (state) => state.notificationSlice.addNotificationError
  );

  const { data: ticketMembership, isLoading: isTicketMembershipLoading } =
    useGetTicketingTicketMembershipsQuery({
      ticketId,
      onError: () => {
        addNotificationError({ message: 'Error fetching ticket membership' });
      },
    });

  const isBoardMember = useMemo(
    () => Boolean(ticketMembership?.result.groupType),
    [ticketMembership]
  );

  // if has UPDATE_TICKET permission, then is agent
  // if has only READ_TICKET permission, then is collaborator
  const collaboratorType: TicketingCollaboratorTypes | null = useMemo(() => {
    const permissions = ticketMembership?.result.permission || [];

    if (hasPermission('UPDATE_TICKET', permissions)) {
      return TicketingCollaboratorTypes.EDITOR;
    }

    if (hasPermission('READ_TICKET', permissions)) {
      return TicketingCollaboratorTypes.CONTRIBUTOR;
    }

    return null;
  }, [ticketMembership]);

  useEffect(() => {
    if (isTicketMembershipLoading) {
      return;
    }

    const redirectUrl = buildTicketingRedirectUrl({
      isAgent,
      isBoardMember,
      ticketId,
      threadId,
      source,
      collaboratorType,
      tab,
      type,
    });

    navigate(redirectUrl, { replace: true });
  }, [
    isAgent,
    navigate,
    source,
    threadId,
    ticketId,
    isBoardMember,
    collaboratorType,
    tab,
    type,
    isTicketMembershipLoading,
  ]);

  return (
    <div
      style={{
        zIndex: 9999,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Spinner />
    </div>
  );
};
